import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CallLogsQueryParams, ExotelApiService, getCallLogsTotal } from '../../../../core/services/exotel/exotel-api.service';
import { LookupDataService } from '../../../../core/services/lookup-data.service';
import { CallLogsViewComponent, CallLog, CallStats, FilterOption } from '../../../../shared/components/call-logs-view/call-logs-view.component';
import { HasPrivilegeDirective } from '../../../../shared/directives/has-privilege.directive';

@Component({
  selector: 'app-call-logs',
  standalone: true,
  imports: [CommonModule, CallLogsViewComponent, HasPrivilegeDirective],
  templateUrl: './call-logs.component.html',
  styleUrl: './call-logs.component.css',
})
export class CallLogsComponent implements OnInit {
  constructor(
    private exotelService: ExotelApiService,
    private lookupDataService: LookupDataService
  ) {}

  // Stats
  stats: CallStats = {
    totalCalls: 0,
    totalCallDuration: '0h 0m',
    avgCallDuration: '0m',
    scheduledCalls: 0
  };

  isLoading: boolean = false;

  // Data
  callLogs: CallLog[] = [];
  exotelCallLogs: any[] = [];
  
  // Options
  telecallerOptions: FilterOption[] = [];

  // Server-side pagination state
  currentPage: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;
  /**
   * The API pages the list, so the table asks us for each page. If the endpoint ever
   * answers with the whole set instead, we hand it over and let the table page locally.
   */
  serverSidePagination: boolean = true;

  // Selected Filters State (needed for API calls)
  selectedDateRanges: FilterOption[] = [];

  ngOnInit(): void {
    this.fetchTelecallers();
    this.fetchCallLogs();
  }

  // Handle Page Change from Shared Component
  onPageChange(event: { page: number; pageSize: number }): void {
    this.currentPage = event.page;
    this.pageSize = event.pageSize;
    this.fetchCallLogs();
  }

  // Handle Date Range Change from Shared Component
  onDateRangeChange(selectedDates: FilterOption[]): void {
    this.selectedDateRanges = selectedDates;
    this.currentPage = 1;
    this.fetchCallLogs();
  }

  fetchTelecallers(): void {
    this.lookupDataService.getTelecallers().subscribe({
      next: (telecallers) => {
        this.telecallerOptions = telecallers.map(t => ({
          id: t.id,
          name: t.name
        }));
      },
      error: (error) => {
        console.error('Error fetching telecallers:', error);
      }
    });
  }

  fetchCallLogs(): void {
    this.isLoading = true;
    this.exotelService.getCallLogs(this.buildApiParams()).subscribe({
      next: (response) => {
        this.isLoading = false;

        if (response && response.calls) {
          this.exotelCallLogs = response.calls;
          this.totalRecords = getCallLogsTotal(response);
          this.serverSidePagination = this.totalRecords > response.calls.length;

          this.callLogs = response.calls.map((call: any) => {
            // Prefer ConversationDuration (actual talk time from webhook) over Duration
            const durationSec = parseInt(call.ConversationDuration || call.Duration || '0');
            const minutes = Math.floor(durationSec / 60);
            const seconds = durationSec % 60;
            const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;

            // Format dates
            const startDate = new Date(call.StartTime);
            const endDate = new Date(call.EndTime);

            // Robust Data Extraction
            let customData: any = {};
            if (call.CustomField) {
              try {
                customData = typeof call.CustomField === 'string' ? JSON.parse(call.CustomField) : call.CustomField;
              } catch (e) {
                // ignore
              }
            }

            // Extract Patient Details
            let patientName = call.patient_name || call.PatientName || customData.patient_name || customData.PatientName;
            let patientId = call.patient_id || call.PatientId || customData.patient_id || customData.PatientId;

            // Extract Telecaller Details
            let telecallerName = call.telecaller_name || call.TelecallerName || customData.telecaller_name || customData.TelecallerName;
            let userId = call.user_id || call.UserId || customData.user_id || customData.UserId;

            // Fallbacks
            if (!patientName) patientName = call.CallerName || 'Unknown Patient';
            if (!telecallerName) telecallerName = call.From || 'Unknown Telecaller';

            // Map status to outcome (including webhook-only statuses ringing/in-progress)
            let outcome: 'COMPLETED' | 'RESCHEDULED' | 'NO_ANSWER' | 'CANCELLED' | 'FAILED' | 'BUSY' = 'COMPLETED';
            if (call.Status) {
              switch (call.Status.toLowerCase()) {
                case 'completed': outcome = 'COMPLETED'; break;
                case 'failed': outcome = 'FAILED'; break;
                case 'no-answer': outcome = 'NO_ANSWER'; break;
                case 'busy': outcome = 'BUSY'; break;
                case 'ringing':
                case 'in-progress': outcome = 'COMPLETED'; break;
              }
            }

            return {
              id: call.Sid,
              callerId: call.From,
              patientName: patientName,
              telecallerName: telecallerName,
              date: startDate.toLocaleDateString('en-GB'),
              startTime: startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
              endTime: endDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
              duration: formattedDuration,
              nextFollowUp: 'N/A',
              callType: call.Direction.includes('outbound') ? 'Outgoing' : 'Incoming',
              outcome: outcome,
              program: 'General',
              recordingUrl: call.RecordingUrl ? this.exotelService.getProxiedRecordingUrl(call.RecordingUrl) : undefined,
              // Additional fields
              patientId: patientId,
              userId: userId,
            };
          });
          
          // Calculate stats
          const totalDurationSec = this.callLogs.reduce((acc, call) => {
            const [min, sec] = call.duration.split(':').map(Number);
            return acc + (min * 60) + sec;
          }, 0);

          const avgDurationSec = this.callLogs.length > 0 ? Math.floor(totalDurationSec / this.callLogs.length) : 0;
          
          this.stats = {
            totalCalls: this.totalRecords,
            totalCallDuration: `${Math.floor(totalDurationSec / 3600)}h ${Math.floor((totalDurationSec % 3600) / 60)}m`,
            avgCallDuration: `${Math.floor(avgDurationSec / 60)}m ${avgDurationSec % 60}s`,
            scheduledCalls: this.callLogs.filter(c => c.outcome === 'RESCHEDULED').length
          };
        }
      },
      error: (error) => {
        console.error('Error fetching call logs:', error);
        this.isLoading = false;
      }
    });
  }

  private buildApiParams(): CallLogsQueryParams {
    const params: CallLogsQueryParams = {
      page: this.currentPage,
      page_size: this.pageSize
    };

    if (this.selectedDateRanges.length > 0) {
      const { startTime, endTime } = this.getDateRangeValues(this.selectedDateRanges[0].id);
      if (startTime) params.start_time = startTime;
      if (endTime) params.end_time = endTime;
    }

    return params;
  }

  private getDateRangeValues(dateRangeId: string): { startTime?: string; endTime?: string } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateRangeId) {
      case 'today':
        return {
          startTime: today.toISOString(),
          endTime: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString()
        };
      case 'yesterday':
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        return {
          startTime: yesterday.toISOString(),
          endTime: today.toISOString()
        };
      case 'last7days':
        const last7Days = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        return {
          startTime: last7Days.toISOString(),
          endTime: now.toISOString()
        };
      case 'last30days':
        const last30Days = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        return {
          startTime: last30Days.toISOString(),
          endTime: now.toISOString()
        };
      case 'thisMonth':
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          startTime: thisMonthStart.toISOString(),
          endTime: now.toISOString()
        };
      case 'lastMonth':
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        return {
          startTime: lastMonthStart.toISOString(),
          endTime: lastMonthEnd.toISOString()
        };
      default:
        return {};
    }
  }
}
